/**
 * src/pages/FallacyLibrary/data/labStatements.ts
 * Bank of 26 realistic fictional statements for the Fallacy Identification Lab.
 * Includes clear fallacies (across all 7 categories), weak inferences,
 * reasonable arguments, unsupported claims, and ambiguous statements needing context.
 */

import type { LabStatement } from "../types";

export const LAB_STATEMENTS: LabStatement[] = [
  // 1. Clear fallacy (Relevance)
  {
    id: "lab-01",
    statement:
      "Town civil engineer Dr. Vance warned that the downtown drainage retention pond will breach its banks during a severe storm. But Vance previously worked for a private contractor that lost a bidding war on this project, so we should ignore her engineering calculations.",
    sourceContext: "Municipal budget hearing deliberation",
    classification: "clear-fallacy",
    associatedFallacyId: "ad-hominem",
    fallacyOptions: ["ad-hominem", "straw-man", "appeal-to-tradition", "false-dilemma"],
    explanation:
      "This is an Ad Hominem fallacy (circumstantial). Vance's past employment history does not invalidate her hydrologic calculations. The council must inspect the engineering data directly.",
    nuanceNotes:
      "A conflict of interest justifies auditing calculations independently, but dismissing mathematical conclusions purely because of the advocate's background is fallacious.",
    categoryTag: "relevance"
  },
  // 2. Clear fallacy (Presumption)
  {
    id: "lab-02",
    statement:
      "Either our city council votes to issue a $400 million municipal bond to construct a six-lane highway bypass immediately, or our entire downtown area will face absolute economic gridlock and complete bankruptcy within three years.",
    sourceContext: "Mayoral campaign debate",
    classification: "clear-fallacy",
    associatedFallacyId: "false-dilemma",
    fallacyOptions: ["false-dilemma", "slippery-slope", "begging-the-question", "circular-reasoning"],
    explanation:
      "This is a False Dilemma. The speaker forces a binary choice between an expensive $400M highway and total municipal bankruptcy, ignoring alternatives like rapid bus lanes or signal optimization.",
    nuanceNotes:
      "While traffic congestion is real, presenting extreme destruction as the only alternative artificially limits rational policy deliberation.",
    categoryTag: "presumption"
  },
  // 3. Clear fallacy (Evidence)
  {
    id: "lab-03",
    statement:
      "I bought an electric Solaria sedan last week, and during yesterday's snowstorm the charging latch froze shut for twenty minutes. That conclusively proves electric vehicle technology is completely useless in cold weather.",
    sourceContext: "Automotive online user review",
    classification: "clear-fallacy",
    associatedFallacyId: "hasty-generalization",
    fallacyOptions: ["hasty-generalization", "anecdotal-evidence", "texas-sharpshooter", "weak-analogy"],
    explanation:
      "This is a Hasty Generalization (and reliance on anecdotal evidence). Drawing sweeping conclusions about an entire global automotive sector from one car during one freeze is an unjustified inductive leap.",
    nuanceNotes:
      "The latch freeze is a genuine design defect on that unit, but generalizing to all EV powertrains in all sub-zero conditions ignores fleet-level Scandinavian winter telemetry.",
    categoryTag: "evidence"
  },
  // 4. Clear fallacy (Causal)
  {
    id: "lab-04",
    statement:
      "Across forty coastal counties, ice cream sales and emergency room drowning resuscitations peaked during the exact same summer months. Therefore, consuming dairy ice cream before swimming induces fatal cramping and drowning.",
    sourceContext: "Public health editorial column",
    classification: "clear-fallacy",
    associatedFallacyId: "correlation-causation",
    fallacyOptions: ["correlation-causation", "post-hoc", "confounding", "reverse-causation"],
    explanation:
      "This is the Fallacy of Confusing Correlation with Causation. Both ice cream consumption and beach swimming rise simultaneously due to a third confounding factor: hot summer temperatures.",
    nuanceNotes:
      "A strong correlation (r > 0.8) indicates co-occurrence, but establishing causal linkage requires identifying an empirical biochemical mechanism and controlling for weather.",
    categoryTag: "causal"
  },
  // 5. Clear fallacy (Language)
  {
    id: "lab-05",
    statement:
      "Universal physical laws, like Newton's law of gravitation, cannot be broken by any mortal human. Our commercial cloud licensing agreement with the city is an established law under municipal code. Therefore, the city cannot legally or physically break this contract.",
    sourceContext: "Corporate arbitration hearing",
    classification: "clear-fallacy",
    associatedFallacyId: "equivocation",
    fallacyOptions: ["equivocation", "amphiboly", "composition", "reification"],
    explanation:
      "This is Equivocation. The speaker switches the meaning of 'law' from an immutable physical regularities discovered by physics to an enforceable legal statute.",
    nuanceNotes:
      "While breaking a contract incurs legal damages, it is physically possible to breach it. Using physical invariances to prove juridical infallibility is a semantic error.",
    categoryTag: "language"
  },
  // 6. Clear fallacy (Formal)
  {
    id: "lab-06",
    statement:
      "If the main underground water pipe ruptures, the retail basements along Elm Street will be flooded with muddy water. We inspected the retail basements today and found them flooded with muddy water. Therefore, the main underground water pipe has definitely ruptured.",
    sourceContext: "Civil insurance claims report",
    classification: "clear-fallacy",
    associatedFallacyId: "affirming-the-consequent",
    fallacyOptions: ["affirming-the-consequent", "denying-the-antecedent", "undistributed-middle", "invalid-conversion"],
    explanation:
      "This is the formal deductive fallacy of Affirming the Consequent (If P then Q; Q; therefore P). Basements can flood from sewer backups, broken sprinklers, or roof drains.",
    nuanceNotes:
      "Flooding provides inductive abductive grounds to investigate the water main, but it does not deductively prove the rupture.",
    categoryTag: "formal"
  },
  // 7. Clear fallacy (Statistical)
  {
    id: "lab-07",
    statement:
      "Corvallia Syndrome has a 1% base rate in our town. This new diagnostic test has a 90% true-positive rate and a 9% false-positive rate. Patient Marcus tested positive, which means he has a 90% probability of having Corvallia Syndrome.",
    sourceContext: "Medical clinic consultation",
    classification: "clear-fallacy",
    associatedFallacyId: "base-rate-neglect",
    fallacyOptions: ["base-rate-neglect", "prosecutors-fallacy", "conjunction-fallacy", "gamblers-fallacy"],
    explanation:
      "This is Base-Rate Neglect. Applying Bayes' Theorem reveals P(Syndrome | Positive) = 90 / (90 + 891) ≈ 9.2%, because healthy false positives massively outnumber true cases in a low-prevalence population.",
    nuanceNotes:
      "A 90% sensitive test feels definitive to lay intuition, but low prior probability dominates diagnostic screening outcomes.",
    categoryTag: "statistical"
  },
  // 8. Reasonable argument (Empirical / Epidemiological)
  {
    id: "lab-08",
    statement:
      "Longitudinal cohort studies tracking 120,000 adults over twenty years, while controlling for diet, age, socioeconomic status, and smoking history, demonstrated that individuals who engaged in at least 150 minutes of moderate aerobic exercise per week experienced a 24% reduction in all-cause cardiovascular mortality compared to sedentary peers.",
    sourceContext: "Peer-reviewed cardiovascular epidemiology review",
    classification: "reasonable-argument",
    explanation:
      "This is a Potentially Reasonable Argument. It relies on a large longitudinal sample, controls for major known confounders, specifies effect sizes, and avoids sweeping or unverified causal claims.",
    nuanceNotes:
      "Observational epidemiology cannot reach 100% mathematical certainty, but controlling for major confounders across large cohorts provides legitimate, sound inductive justification.",
    categoryTag: "general"
  },
  // 9. Reasonable argument (Relevant expert testimony)
  {
    id: "lab-09",
    statement:
      "Dr. Angela Moreno, chief seismologist at the State Geological Survey who has published over 40 peer-reviewed papers on the Hayward Fault zone, testified that geological core samples show stress accumulation consistent with a major seismic rupture within the next 30 years.",
    sourceContext: "Legislative committee on infrastructure resilience",
    classification: "reasonable-argument",
    explanation:
      "This is a Potentially Reasonable Argument (sound epistemic deference to relevant expertise). Dr. Moreno is testifying strictly within her verified domain of expertise, citing empirical core samples.",
    nuanceNotes:
      "Deferring to expert consensus in an established scientific field where the specialist is qualified is sound epistemic practice, not an appeal to irrelevant authority.",
    categoryTag: "general"
  },
  // 10. Weak inference (Suggestive but insufficient)
  {
    id: "lab-10",
    statement:
      "In a laboratory dish, compound X-42 inhibited the replication of influenza virus cells by 80% without destroying healthy mammalian tissue. Therefore, prescribing compound X-42 to human patients with the flu will quickly cure influenza infections across the population.",
    sourceContext: "Pharmaceutical press release",
    classification: "weak-inference",
    explanation:
      "This is a Weak Inference. In vitro cellular assays frequently fail to translate to in vivo human efficacy due to bioavailability, liver metabolism, toxic degradation, and immune response barriers.",
    nuanceNotes:
      "The in vitro finding is a real, promising scientific clue, but leaping from a petri dish to human clinical efficacy is an over-extended inductive inference.",
    categoryTag: "general"
  },
  // 11. Unsupported claim (Assertion without backing)
  {
    id: "lab-11",
    statement:
      "Installing hexagonal acoustic diffusers on the ceilings of municipal elementary school classrooms increases mathematical cognitive absorption by 40% in third-grade pupils.",
    sourceContext: "Acoustic tile sales catalogue",
    classification: "unsupported-claim",
    explanation:
      "This is an Unsupported Claim. It asserts a highly specific, quantitative causal effect without providing any clinical trials, sample sizes, methodology, or experimental citations.",
    nuanceNotes:
      "The claim may or may not be true, but as presented, it carries zero epistemic backing. Asserting precise percentages without documentation is an unsubstantiated marketing claim.",
    categoryTag: "general"
  },
  // 12. Ambiguous, needs more context
  {
    id: "lab-12",
    statement:
      "The new regional hospital protocol resulted in a 50% decrease in post-operative infection incidents during the third quarter.",
    sourceContext: "Hospital administrative newsletter",
    classification: "ambiguous-context",
    explanation:
      "This statement is Ambiguous and Needs More Context. We cannot evaluate whether this is clinically meaningful without knowing the baseline absolute risk (e.g., did infections drop from 2 to 1, or from 200 to 100?).",
    nuanceNotes:
      "Relative percentage drops (50%) hide absolute numbers. If baseline infections dropped from 2 cases in 10,000 to 1 in 10,000, random variance could explain it; if from 200 to 100, it represents a major clinical achievement.",
    categoryTag: "general"
  },
  // 13. Clear fallacy (Straw Man)
  {
    id: "lab-13",
    statement:
      "The chief librarian suggested trimming evening hours on Tuesdays by 45 minutes to balance the branch staffing budget. A local resident objected: 'The librarian wants to shut down our town library, burn the books, and leave our children illiterate!'",
    sourceContext: "Public library board meeting",
    classification: "clear-fallacy",
    associatedFallacyId: "straw-man",
    fallacyOptions: ["straw-man", "red-herring", "ad-hominem", "slippery-slope"],
    explanation:
      "This is a classic Straw Man fallacy. The speaker exaggerated a 45-minute schedule trim into a cartoonish catastrophe: book-burning and permanent library closure.",
    nuanceNotes:
      "The resident substitutes an indefensible, extreme caricature for the modest operational adjustment actually proposed.",
    categoryTag: "relevance"
  },
  // 14. Clear fallacy (Tu Quoque)
  {
    id: "lab-14",
    statement:
      "Pulmonologist Dr. Thorne gave a lecture showing clinical data that vaping aerosol induces alveolar macrophage inflammation. A student shouted: 'Why should we listen to you? I saw you smoking a cigar at the country club alumni dinner last Saturday!'",
    sourceContext: "University health seminar",
    classification: "clear-fallacy",
    associatedFallacyId: "tu-quoque",
    fallacyOptions: ["tu-quoque", "ad-hominem", "genetic-fallacy", "appeal-to-tradition"],
    explanation:
      "This is Tu Quoque (Appeal to Hypocrisy). Dr. Thorne's personal recreational habits do not alter the cellular toxicology data on macrophage inflammation.",
    nuanceNotes:
      "Hypocrisy is interpersonally frustrating, but a hypocrite's empirical scientific data can be completely sound and valid.",
    categoryTag: "relevance"
  },
  // 15. Clear fallacy (Red Herring)
  {
    id: "lab-15",
    statement:
      "When investigative reporters asked the chemical company CEO why toxic benzene runoff had exceeded state environmental safety thresholds by 300%, the CEO replied: 'Our company employs 2,400 local workers and just donated $500,000 to the municipal pediatric hospital wing.'",
    sourceContext: "Press conference following river contamination",
    classification: "clear-fallacy",
    associatedFallacyId: "red-herring",
    fallacyOptions: ["red-herring", "straw-man", "appeal-to-popularity", "equivocation"],
    explanation:
      "This is a Red Herring. Corporate philanthropy and local employment numbers have zero bearing on whether wastewater benzene levels violated environmental statutes.",
    nuanceNotes:
      "The CEO introduces emotionally admirable topics to derail attention away from the unaddressed chemical spill.",
    categoryTag: "relevance"
  },
  // 16. Clear fallacy (Slippery Slope)
  {
    id: "lab-16",
    statement:
      "If we allow the high school cafeteria to offer plant-based oat milk as an alternative to cow milk, next they will ban all dairy, then ban meat, then prohibit all animal farming statewide, and our agrarian economy will be completely destroyed by next winter!",
    sourceContext: "School district board hearing",
    classification: "clear-fallacy",
    associatedFallacyId: "slippery-slope",
    fallacyOptions: ["slippery-slope", "false-dilemma", "hasty-generalization", "post-hoc"],
    explanation:
      "This is a Slippery Slope fallacy. It posits an unproven, extreme multi-step causal cascade from an optional beverage choice to the complete statewide destruction of agriculture.",
    nuanceNotes:
      "No evidence or institutional mechanisms are supplied to justify why an optional beverage would inevitably mandate a total statewide ban.",
    categoryTag: "presumption"
  },
  // 17. Clear fallacy (Gambler's Fallacy)
  {
    id: "lab-17",
    statement:
      "This fair coin has landed on Heads ten times consecutively. Therefore, on the eleventh flip, the probability of landing on Tails is vastly higher than 50% because the laws of probability require balance.",
    sourceContext: "Classroom probability discussion",
    classification: "clear-fallacy",
    associatedFallacyId: "gamblers-fallacy",
    fallacyOptions: ["gamblers-fallacy", "base-rate-neglect", "conjunction-fallacy", "prosecutors-fallacy"],
    explanation:
      "This is the Gambler's Fallacy. Each flip of an independent fair coin has exactly 50% probability. The coin has no physical memory of previous flips.",
    nuanceNotes:
      "While a sequence of 11 Heads is rare prior to flipping, once 10 Heads have occurred, the 11th flip remains completely independent.",
    categoryTag: "statistical"
  },
  // 18. Clear fallacy (Undistributed Middle)
  {
    id: "lab-18",
    statement:
      "All radical anarchists advocate for replacing the corporate income tax. Economist Arthur Vance advocates for replacing the corporate income tax with a consumption levy. Therefore, Economist Arthur Vance is a radical anarchist.",
    sourceContext: "Political opinion editorial",
    classification: "clear-fallacy",
    associatedFallacyId: "undistributed-middle",
    fallacyOptions: ["undistributed-middle", "affirming-the-consequent", "invalid-conversion", "denying-the-antecedent"],
    explanation:
      "This is the formal deductive fallacy of the Undistributed Middle. The middle term ('tax reformers') is not distributed to all members in either premise, so Vance and anarchists need not overlap.",
    nuanceNotes:
      "Sharing an overlapping policy preference with a group does not deductively place someone into that group's radical ideology.",
    categoryTag: "formal"
  },
  // 19. Clear fallacy (Denying the Antecedent)
  {
    id: "lab-19",
    statement:
      "If the engine oil leaks completely out of the crankcase, the automotive engine will seize up. Our mechanic verified that the crankcase is completely full of oil. Therefore, the engine cannot seize up.",
    sourceContext: "Automotive repair arbitration",
    classification: "clear-fallacy",
    associatedFallacyId: "denying-the-antecedent",
    fallacyOptions: ["denying-the-antecedent", "affirming-the-consequent", "incorrect-conditional-inference", "equivocation"],
    explanation:
      "This is the formal fallacy of Denying the Antecedent (If P then Q; not P; therefore not Q). Engines can seize from broken timing chains, blown head gaskets, or water pump failures.",
    nuanceNotes:
      "Full oil rules out oil starvation, but oil starvation was only one sufficient condition for engine failure, not the only necessary one.",
    categoryTag: "formal"
  },
  // 20. Clear fallacy (Conjunction Fallacy)
  {
    id: "lab-20",
    statement:
      "Laura is an award-winning organic gardener and environmental activist who studies soil hydrology. It is more probable that Laura is an accountant who manages an urban compost cooperative than that Laura is simply an accountant.",
    sourceContext: "Psychology student case evaluation",
    classification: "clear-fallacy",
    associatedFallacyId: "conjunction-fallacy",
    fallacyOptions: ["conjunction-fallacy", "base-rate-neglect", "prosecutors-fallacy", "gamblers-fallacy"],
    explanation:
      "This is the Conjunction Fallacy. P(Accountant and Composter) cannot mathematically exceed P(Accountant), because the set of accountants includes the subset of compost-managing accountants.",
    nuanceNotes:
      "Adding evocative, realistic details increases narrative plausibility, but strictly reduces mathematical probability.",
    categoryTag: "statistical"
  },
  // 21. Reasonable argument (Valid Modus Tollens)
  {
    id: "lab-21",
    statement:
      "If the digital network security gateway is operating normally, all unauthorized external root login requests generate an encrypted log entry in the security audit database within 500 milliseconds. An unauthorized root login occurred at 3:14 AM yesterday, but zero log entries exist in the database. Therefore, the network security gateway was not operating normally at that time.",
    sourceContext: "Cybersecurity incident response post-mortem",
    classification: "reasonable-argument",
    explanation:
      "This is a Potentially Reasonable Argument (specifically, a valid deductive Modus Tollens: If P then Q; Not Q; therefore Not P). The logic is formally valid and empirically grounded.",
    nuanceNotes:
      "If the premises are factually accurate, the conclusion follows with deductive necessity.",
    categoryTag: "formal"
  },
  // 22. Clear fallacy (Composition)
  {
    id: "lab-22",
    statement:
      "Every single component inside this high-performance racing engine was manufactured from ultra-lightweight carbon fiber weighing under one pound. Therefore, the completely assembled racing engine must weigh under one pound.",
    sourceContext: "Mechanics hobbyist forum",
    classification: "clear-fallacy",
    associatedFallacyId: "composition",
    fallacyOptions: ["composition", "division", "hasty-generalization", "reification"],
    explanation:
      "This is the Fallacy of Composition. The collective whole does not inherit the specific dimensional or weight properties of its individual constituent parts.",
    nuanceNotes:
      "Combining hundreds of sub-one-pound parts results in an assembled engine weighing hundreds of pounds.",
    categoryTag: "language"
  },
  // 23. Weak inference (Small, unblinded pilot)
  {
    id: "lab-23",
    statement:
      "In an unblinded pilot study of six university athletes who drank beetroot juice before running a 5K sprint, five reported feeling less fatigued in their quadriceps. We conclude that beetroot juice dramatically enhances aerobic athletic stamina in all human runners.",
    sourceContext: "Sports nutrition blog post",
    classification: "weak-inference",
    explanation:
      "This is a Weak Inference. An unblinded sample of six athletes with subjective self-reported fatigue metrics is vulnerable to placebo effects, expectation bias, and high sampling error.",
    nuanceNotes:
      "While nitric oxide in beetroot juice has plausible biological activity, six unblinded runners do not justify generalizing to all athletes.",
    categoryTag: "evidence"
  },
  // 24. Ambiguous, needs more context
  {
    id: "lab-24",
    statement:
      "Installing speed enforcement cameras along the Northern Parkway reduced fatal collisions by 30% over the subsequent calendar year.",
    sourceContext: "Traffic safety press release",
    classification: "ambiguous-context",
    explanation:
      "This is Ambiguous and Needs More Context. Did traffic volume change during that year? Was there regression to the mean following an unusual spike? Did road resurfacing happen simultaneously?",
    nuanceNotes:
      "A 30% reduction is encouraging, but without baseline crash frequency, vehicle volume adjustments, and comparison corridors, causality remains ambiguous.",
    categoryTag: "causal"
  },
  // 25. Clear fallacy (Post Hoc)
  {
    id: "lab-25",
    statement:
      "Governor Vance signed the statewide corporate technology tax rebate on June 1st. On June 15th, international oil prices plummeted by $40 a barrel on global commodities markets. Therefore, Vance's tax rebate successfully lowered global energy prices.",
    sourceContext: "Political television campaign commercial",
    classification: "clear-fallacy",
    associatedFallacyId: "post-hoc",
    fallacyOptions: ["post-hoc", "correlation-causation", "confounding", "slippery-slope"],
    explanation:
      "This is Post Hoc Ergo Propter Hoc. The state tax rebate happened before the global oil price collapse, but global oil prices are governed by international OPEC quotas, geopolitical supply chains, and global demand.",
    nuanceNotes:
      "A state tax policy lacks the economic magnitude to unilaterally move worldwide oil prices; mere chronological sequence does not establish causality.",
    categoryTag: "causal"
  },
  // 26. Reasonable argument (Controlled clinical meta-analysis)
  {
    id: "lab-26",
    statement:
      "A Cochrane systematic review combining 18 randomized, double-blind, placebo-controlled trials encompassing 8,400 adult hypertensive patients concluded that thiazide diuretics produce a statistically significant and clinically meaningful average reduction of 8 mmHg in systolic blood pressure compared to placebo.",
    sourceContext: "Evidence-based clinical guidelines",
    classification: "reasonable-argument",
    explanation:
      "This is a Potentially Reasonable Argument. It synthesizes multiple pre-registered, blinded randomized trials across large samples, reporting specific effect sizes and comparison controls.",
    nuanceNotes:
      "Systematic reviews of randomized controlled trials represent the highest tier of clinical empirical evidence.",
    categoryTag: "general"
  }
];
